import { Grid, Link, Stack, Typography, useTheme } from "@mui/material";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { AquariusCard } from "@smartrr/shared/customerPortal/components/AquariusCard";
import { AquariusIconButton } from "@smartrr/shared/customerPortal/components/AquariusIconButton";
import { DotSpinner } from "@smartrr/shared/customerPortal/components/DotSpinner";
import { noawait } from "@smartrr/shared/utils/noawait";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { ArrowForwardThick } from "akar-icons";
import React, { useEffect, useRef, useState } from "react";
import {
  EmailShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  // FacebookShareButton,
  // InstapaperShareButton,
} from "react-share";
import { toast } from "react-toastify";

import { LoyaltyStoreAccess } from "../../store";
import { LoyaltyApi } from "@smartrr/shared/interfaces/loyalty/api";
import { prefixClassName } from "@smartrr/shared/components/primitives";

export interface IReferralCardProps {
  referralName?: string;
  referralDescription?: string;
  pointsInReward?: number;
  pointsForFriend?: {
    type: "fixed_amount" | "percentage";
    value: string;
  };
}

const SMSShareLink: React.FC<{ url: string | null; children?: React.ReactNode }> = ({ url, children }) => {
  if (!url) {
    return <div style={{ opacity: "0.6" }}>{children}</div>;
  }
  return <Link href={`sms:?&body=${url}`}>{children}</Link>;
};

export const ReferralCard = () => {
  const referral = LoyaltyStoreAccess.useReferralProgram();
  if (!referral) {
    return null;
  }

  return <ReferralCardContent referral={referral} />;
};

interface ReferralCardContentProps {
  referral: LoyaltyApi.Referral.Type;
}

const ReferralCardContent = ({ referral }: ReferralCardContentProps) => {
  const theme = useTheme();

  const actions = LoyaltyStoreAccess.useActions();
  const isLoading = LoyaltyStoreAccess.useIsLoading();
  const isUsingSpecialReferrals = !!LoyaltyStoreAccess.useSpecialReferralsVariantId();
  const referralLink = LoyaltyStoreAccess.useReferralLink();
  const { symbol: currencySymbol } = LoyaltyStoreAccess.useCurrency();
  const pointsText = LoyaltyStoreAccess.useText(referral.pointsInReward);

  const [isLinkCopied, setIsLinkCopied] = useState(false);

  const stackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    noawait(async () => await actions.fetchReferralLink());
  }, []);

  useEffect(() => {
    if (isLinkCopied) {
      setTimeout(() => {
        setIsLinkCopied(false);
      }, 2000);
    }
  }, [isLinkCopied]);

  const copyReferralCode = () => {
    if (referralLink?.code) {
      navigator.clipboard.writeText(referralLink.code).then(() => {
        setIsLinkCopied(true);
        toast.info(typedLocalize("general.copiedToClipboard"));
      });
    } else {
      toast.error(typedLocalize("loyalty.referralLinkNotFound"));
    }
  };

  const friendGetsReferralCopy =
    referral.priceRule.type === "fixed_amount"
      ? typedLocalize("loyalty.yourFriendGetsUnitsOfValue", {
          amount: -referral.priceRule.value,
          unitName: currencySymbol,
        })
      : typedLocalize("loyalty.yourFriendGetsPercentOfValue", {
          amount: -referral.priceRule.value,
          unitName: "%",
        });

  return (
    <Grid
      item
      xs={12}
      md={4}
      sx={{
        transition: ".3s ease-in-out",
      }}
      className={prefixClassName("referral-container")}
    >
      <AquariusCard sx={{ height: "100%" }}>
        {isLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: stackRef.current?.clientHeight }}>
            <DotSpinner />
          </Stack>
        ) : (
          <Stack direction="column" spacing="15px" sx={{ width: "100%" }} ref={stackRef}>
            <Stack direction="row" alignItems="center" spacing="9px">
              <ArrowForwardThick strokeWidth={1.5} size={22} />
              <Typography variant="h4">{referral.name}</Typography>
            </Stack>
            <Typography variant="body1">{referral.description}</Typography>
            <AquariusCard
              sx={{
                border: `${theme.modernSmallCardBorderWidth}px solid ${theme.modernSmallCardBorderColor} !important`,
                borderRadius: `${theme.modernSmallCardBorderRadius}px !important`,
                width: "100%",
              }}
            >
              <Stack direction="column" spacing="7px">
                <Stack direction="row" justifyContent="space-between" spacing="16px">
                  <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                    {typedLocalize("loyalty.yourFriendGets")}
                  </Typography>
                  {referral.priceRule.value ? (
                    <Typography variant="body2" sx={{ textAlign: "right" }}>
                      {friendGetsReferralCopy}
                    </Typography>
                  ) : null}
                </Stack>
                <hr />
                <Stack direction="row" justifyContent="space-between" spacing="16px">
                  <Typography variant="body1">{typedLocalize("loyalty.youGet")}</Typography>
                  <Typography variant="body2" sx={{ textAlign: "right" }}>
                    {isUsingSpecialReferrals ? friendGetsReferralCopy : pointsText}
                  </Typography>
                </Stack>
              </Stack>
            </AquariusCard>
            <AquariusButton
              filled
              isLoading={isLoading}
              disabled={isLinkCopied || referralLink === undefined}
              onClick={copyReferralCode}
              className={prefixClassName("code-copy-button")}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: `${
                    !referralLink || isLinkCopied
                      ? theme.modernCardBackground
                      : theme.modernPrimaryButtonFontColor
                  } !important`,
                }}
              >
                {isLoading ? (
                  <DotSpinner color={theme.modernCardBackground} />
                ) : isLinkCopied ? (
                  typedLocalize("general.copied")
                ) : (
                  typedLocalize("loyalty.referralCode")
                )}
              </Typography>
            </AquariusButton>
            <Stack direction="row" justifyContent="space-between" sx={{ width: "100%" }}>
              <EmailShareButton url={referralLink?.link ?? ""} disabled={!referralLink}>
                <AquariusIconButton iconName="mail" size="small" noButton />
              </EmailShareButton>
              <SMSShareLink url={referralLink?.link ?? ""}>
                <AquariusIconButton iconName="chat" size="small" noButton />
              </SMSShareLink>
              <TwitterShareButton url={referralLink?.link ?? ""} disabled={!referralLink}>
                <AquariusIconButton
                  iconName="twitter"
                  size="small"
                  sx={{ "& svg": { strokeWidth: "0px !important", fill: theme.modernIconsColor + "!important" } }}
                  noButton
                />
              </TwitterShareButton>
              <WhatsappShareButton url={referralLink?.link ?? ""} disabled={!referralLink}>
                <AquariusIconButton
                  iconName="whatsapp"
                  size="small"
                  sx={{ "& svg": { strokeWidth: "0px !important", fill: theme.modernIconsColor + "!important" } }}
                  noButton
                />
              </WhatsappShareButton>
            </Stack>
          </Stack>
        )}
      </AquariusCard>
    </Grid>
  );
};
